/**
 * The exam workspaces' sittings, built from the catalogue's sets.
 *
 * The server sends each part with its set (`set_id`, `set_title`) and its
 * place in it (`position`, 1-based, the exam's layout order), in catalogue
 * order. A set is one sitting: its parts in position order, each part the
 * module its position names in the exam's layout. Nothing is grouped or
 * classified from ids.
 *
 * Each builder returns the list the workspace has always taken: a sitting
 * object (`kind: 'sitting'` for ESAT and TARA) for a set it can sit as a
 * whole, and the set's parts on their own for one it cannot, such as TARA's
 * Beginnings mocks, which hold Problem Solving alone. The workspaces tell
 * the two apart by `kind === 'sitting'`; a part keeps the server's `kind`.
 *
 * A sitting's title is the set's title read the way the page has always
 * named sittings (the module and paper words dropped). A set the backfill
 * titled from its first part reads the same as one it titled itself.
 */

import { ESAT_OPTIONAL_KEYS } from './classifyEsatModule';
import { TARA_MODULE_KEYS } from './classifyTaraPaperModule';
import {
  BMAT_FALLBACK_QUESTION_COUNT,
  BMAT_MODULE_KEYS,
  BMAT_SECTION_MINUTES,
} from './classifyBmatPaperSection';
import { getUcatModuleDurationMinutes } from './getUcatModuleDurationMinutes';
import { UCAT_SUBTEST_KEYS } from './ucatSubtests';

// The module at each position of a set, per exam (the server's EXAM_LAYOUTS).
const ESAT_LAYOUT = ['mathematics_1', 'physics', 'chemistry', 'biology', 'mathematics_2'];
const TSA_SECTIONS = ['s1', 's2'];

/** The catalogue's sets, in catalogue order, each with its parts in position order. */
export function groupCatalogueSets(parts) {
  const sets = [];
  const byId = new Map();
  (parts || []).forEach((part) => {
    if (!part) return;
    const setId = part.set_id == null ? `part:${part.id}` : String(part.set_id);
    let set = byId.get(setId);
    if (!set) {
      set = { setId, title: part.set_title || part.display_name || part.id, parts: [] };
      byId.set(setId, set);
      sets.push(set);
    }
    set.parts.push(part);
  });
  sets.forEach((set) => {
    set.parts.sort((a, b) => (Number(a.position) || 0) - (Number(b.position) || 0));
  });
  return sets;
}

/**
 * The set's parts keyed by the module their position names in `layout`, or
 * null when a part has no place in it (no position, one outside the layout,
 * or two parts at one position): such a set is listed as its parts.
 */
function partsByModule(set, layout) {
  const out = {};
  const placed = set.parts.every((part) => {
    const key = layout[(Number(part.position) || 0) - 1];
    if (!key || out[key]) return false;
    out[key] = part;
    return true;
  });
  return placed ? out : null;
}

function wholeSet(set, layout) {
  const byModule = partsByModule(set, layout);
  return Boolean(byModule) && layout.every((key) => byModule[key]);
}

function setText(set) {
  return `${set.title} ${set.parts.map((p) => p.display_name || '').join(' ')}`;
}

function yearOf(text) {
  const year = String(text || '').match(/20\d{2}/) || String(text || '').match(/19\d{2}/);
  return year ? year[0] : null;
}

// A part listed on its own. A catalogue part carries its own `kind`, which wins.
const asPaper = (part) => ({ kind: 'paper', ...part });

// --- TMUA ----------------------------------------------------------------

function sittingTitleFromDisplayName(displayName) {
  return String(displayName || '')
    .replace(/\s*[—–-]\s*Paper\s*1\/2\s*$/i, '')
    .replace(/\s*[—–-]\s*Paper\s*[12]\s*$/i, '')
    .trim();
}

function tmuaSitting(title, byModule) {
  const { p1: paper1 = null, p2: paper2 = null } = byModule;
  const start = paper1 || paper2;
  return {
    paper1,
    paper2,
    startId: start?.id,
    startCatalogId: start?.catalog_id || start?.id,
    title: sittingTitleFromDisplayName(title),
    questionCount: (paper1?.question_count || 0) + (paper2?.question_count || 0),
    duration: paper1 && paper2 ? '150 min' : (start?.duration || '75 min'),
    completed: false,
    tier: start?.tier,
    tierLabel: start?.tierLabel,
    difficulty: start?.difficulty,
  };
}

/** One sitting per TMUA set: Paper 1 then Paper 2, or one of them. */
export function buildTmuaSittings(parts) {
  const out = [];
  groupCatalogueSets(parts).forEach((set) => {
    const byModule = partsByModule(set, ['p1', 'p2']);
    if (byModule) out.push(tmuaSitting(set.title, byModule));
    else out.push(...set.parts);
  });
  return out;
}

// --- LNAT ----------------------------------------------------------------

// The page has always named an LNAT Beginnings mock without its tier
// ("LNAT Mock 3"), the set "LNAT Beginnings Mock 3".
function sittingTitleFromLnatDisplayName(displayName) {
  return String(displayName || '')
    .replace(/^LNAT Beginnings Mock\b/i, 'LNAT Mock')
    .replace(/\s*[—–-]\s*Paper\s*(\d+)\s*,\s*Section\s*[AB]\s*$/i, ' Paper $1')
    .replace(/\s*[—–-]\s*Section\s*[AB]\s*$/i, '')
    .replace(/\s*,\s*Section\s*[AB]\s*$/i, '')
    .replace(/\s+Section\s*[AB]\s*$/i, '')
    .trim();
}

function minutesOf(value, fallback) {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) return value;
  const match = String(value || '').match(/(\d+)/);
  return match ? Number(match[1]) : fallback;
}

/**
 * One sitting per LNAT set that holds Section A and Section B. The parts of
 * any other past set on their own. A mock set without both sections is not
 * listed: the page has always listed whole LNAT mocks only, so a Section A
 * alone (Beginnings Mock 20) is not shown even while its set is active.
 */
export function buildLnatSittings(parts) {
  const out = [];
  groupCatalogueSets(parts).forEach((set) => {
    if (!wholeSet(set, ['A', 'B'])) {
      out.push(...set.parts.filter((part) => part.category !== 'mock_papers'));
      return;
    }
    const { A: paper1, B: paper2 } = partsByModule(set, ['A', 'B']);
    const aCount = Number(paper1.question_count);
    out.push({
      paper1,
      paper2,
      startId: paper1.id,
      startCatalogId: paper1.catalog_id || paper1.id,
      pearsonSitting: true,
      title: sittingTitleFromLnatDisplayName(set.title),
      questionCount: (Number.isFinite(aCount) && aCount > 0 ? aCount : 42) + 3,
      duration: `${minutesOf(paper1.duration, 95) + minutesOf(paper2.duration, 40)} min`,
      hasEssayModule: true,
      completed: false,
      tier: paper1.tier,
      tierLabel: paper1.tierLabel,
      difficulty: paper1.difficulty,
    });
  });
  return out;
}

// --- UCAT ----------------------------------------------------------------

export function sittingTitleFromUcatDisplayName(displayName) {
  return String(displayName || '')
    .replace(/\bVerbal Reasoning\b/gi, '')
    .replace(/\bDecision Making\b/gi, '')
    .replace(/\bQuantitative Reasoning\b/gi, '')
    .replace(/\bSituational Judgement\b/gi, '')
    .replace(/\bSection\s*[1-4]\b/gi, '')
    .replace(/\bUCAT\b/gi, '')
    .replace(/\s*[—–-]\s*/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

const UCAT_FALLBACK_COUNTS = {
  verbal_reasoning: 44,
  decision_making: 35,
  quantitative_reasoning: 36,
  situational_judgement: 69,
};

function ucatSitting(set, papers) {
  const vr = papers.verbal_reasoning;
  const questionCount = UCAT_SUBTEST_KEYS.reduce((sum, key) => {
    const count = Number(papers[key]?.question_count);
    return sum + (Number.isFinite(count) && count > 0 ? count : UCAT_FALLBACK_COUNTS[key]);
  }, 0);
  const minutes = UCAT_SUBTEST_KEYS.reduce((sum, key) => sum + getUcatModuleDurationMinutes({
    moduleKey: key,
    duration_minutes: papers[key]?.duration_minutes,
    duration: papers[key]?.duration,
  }), 0);
  return {
    papers,
    startId: vr.id,
    startCatalogId: vr.catalog_id || vr.id,
    pearsonSitting: true,
    ucatSiblings: {
      decision_making: papers.decision_making,
      quantitative_reasoning: papers.quantitative_reasoning,
      situational_judgement: papers.situational_judgement,
    },
    title: sittingTitleFromUcatDisplayName(set.title),
    questionCount,
    duration: Number.isFinite(minutes) && minutes > 0 ? `${minutes} min` : '111 min',
    completed: false,
    tier: vr.tier,
    tierLabel: vr.tierLabel,
    difficulty: vr.difficulty,
  };
}

/** One sitting per UCAT set holding all four subtests; the parts of any other set on their own. */
export function buildUcatSittings(parts) {
  const out = [];
  groupCatalogueSets(parts).forEach((set) => {
    if (!wholeSet(set, UCAT_SUBTEST_KEYS)) {
      out.push(...set.parts);
      return;
    }
    out.push(ucatSitting(set, partsByModule(set, UCAT_SUBTEST_KEYS)));
  });
  return out;
}

// --- ESAT ----------------------------------------------------------------

function sittingTitleFromEsatDisplayName(displayName) {
  return String(displayName || '')
    .replace(/\bMathematics\s*2\b/gi, '')
    .replace(/\bMaths\s*2\b/gi, '')
    .replace(/\bMath\s*2\b/gi, '')
    .replace(/\bMathematics\s*1\b/gi, '')
    .replace(/\bMaths\s*1\b/gi, '')
    .replace(/\bMath\s*1\b/gi, '')
    .replace(/\bBiology\b/gi, '')
    .replace(/\bChemistry\b/gi, '')
    .replace(/\bPhysics\b/gi, '')
    .replace(/\s*[—–-]\s*/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** ENGAA or NSAA for a set of their past papers filed under ESAT, else null. */
function esatHeritage(set) {
  const text = setText(set).toUpperCase();
  if (/\bENGAA\b/.test(text)) return 'ENGAA';
  if (/\bNSAA\b/.test(text)) return 'NSAA';
  return null;
}

function esatQuestionCount(maths1, optionals) {
  const counts = [
    Number(maths1?.question_count),
    ...Object.values(optionals || {}).map((p) => Number(p?.question_count)),
  ].filter((n) => Number.isFinite(n) && n > 0);
  if (!counts.length) return 81;
  // Display uses Maths 1 + up to two optionals (picker / ENGAA fixed).
  const modulesSat = 1 + Math.min(2, Object.keys(optionals || {}).length);
  if (counts.length >= modulesSat) return counts.slice(0, modulesSat).reduce((a, b) => a + b, 0);
  const avg = Math.round(counts.reduce((a, b) => a + b, 0) / counts.length);
  return avg * modulesSat;
}

/**
 * One sitting per ESAT set that holds Mathematics 1 and its optional modules
 * (two at least; ENGAA's one, Physics); the parts of any other set on their
 * own.
 */
export function buildEsatSittings(parts) {
  const out = [];
  groupCatalogueSets(parts).forEach((set) => {
    const byModule = partsByModule(set, ESAT_LAYOUT) || {};
    const maths1 = byModule.mathematics_1 || null;
    const optionals = {};
    ESAT_OPTIONAL_KEYS.forEach((key) => {
      if (byModule[key]) optionals[key] = byModule[key];
    });
    const optionalCount = Object.keys(optionals).length;
    const heritage = esatHeritage(set);
    if (!maths1 || !(optionalCount >= 2 || (heritage && optionalCount >= 1))) {
      out.push(...set.parts.map(asPaper));
      return;
    }
    const year = yearOf(`${maths1.display_name || ''} ${set.title}`);
    out.push({
      kind: 'sitting',
      startId: maths1.id,
      startCatalogId: maths1.catalog_id || maths1.id,
      title: heritage && year ? `${heritage} ${year}` : sittingTitleFromEsatDisplayName(set.title),
      questionCount: esatQuestionCount(maths1, optionals),
      duration: `${(1 + Math.min(2, optionalCount)) * 40} min`,
      tier: maths1.tier,
      tierLabel: maths1.tierLabel,
      difficulty: maths1.difficulty,
      completed: false,
      pearsonSitting: true,
      paper1: maths1,
      mathematics1: maths1,
      optionals,
      heritageExam: heritage || null,
      year: year || null,
      // ENGAA composition is fixed; NSAA/native always go through the picker helper.
      fixedOptionalKeys: heritage === 'ENGAA' ? Object.keys(optionals) : null,
    });
  });
  return out;
}

// --- TARA, BMAT and TSA --------------------------------------------------

function sittingTitleFromTaraDisplayName(displayName) {
  return String(displayName || '')
    .replace(/\bCritical Thinking\b/gi, '')
    .replace(/\bProblem Solving\b/gi, '')
    .replace(/\bWrit(?:ing|ten) Task\b/gi, '')
    .replace(/\bSection\s*[123]\b/gi, '')
    .replace(/\s*[—–-]\s*/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** BMAT or TSA for a set of their past papers filed under TARA, else null. */
function taraHeritage(set) {
  const text = setText(set).toUpperCase();
  if (/\bBMAT\b/.test(text)) return 'BMAT';
  if (/\bTSA\b/.test(text)) return 'TSA';
  return null;
}

function taraSitting(title, modules) {
  const criticalThinking = modules.critical_thinking;
  const count = (paper) => {
    const n = Number(paper?.question_count);
    return Number.isFinite(n) && n > 0 ? n : 22;
  };
  return {
    kind: 'sitting',
    pearsonSitting: true,
    startId: criticalThinking.id,
    startCatalogId: criticalThinking.catalog_id || criticalThinking.id,
    paper1: criticalThinking,
    taraModules: {
      critical_thinking: modules.critical_thinking,
      problem_solving: modules.problem_solving,
      writing: modules.writing,
    },
    title: sittingTitleFromTaraDisplayName(title),
    questionCount: count(modules.critical_thinking) + count(modules.problem_solving) + 1,
    duration: '120 min',
    completed: false,
    tier: criticalThinking.tier,
    tierLabel: criticalThinking.tierLabel,
    difficulty: criticalThinking.difficulty,
  };
}

function bmatSitting(set, modules) {
  const presentKeys = BMAT_MODULE_KEYS.filter((key) => modules[key]?.id);
  const start = modules[presentKeys[0]];
  const year = yearOf(setText(set));
  const bmatModules = {};
  presentKeys.forEach((key) => { bmatModules[key] = modules[key]; });
  const sectionCount = (key) => {
    const n = Number(modules[key]?.question_count);
    return Number.isFinite(n) && n > 0 ? n : (BMAT_FALLBACK_QUESTION_COUNT[key] || 0);
  };
  return {
    kind: 'sitting',
    pearsonSitting: true,
    bmatSitting: true,
    heritageExam: 'BMAT',
    bmatModules,
    paper1: start,
    startId: start?.id,
    startCatalogId: start?.catalog_id || start?.id,
    title: year ? `BMAT ${year}` : (start?.display_name || start?.id),
    questionCount: presentKeys.reduce((sum, key) => sum + sectionCount(key), 0),
    duration: `${presentKeys.reduce((sum, key) => sum + (BMAT_SECTION_MINUTES[key] || 0), 0)} min`,
    completed: false,
    tier: start?.tier,
    tierLabel: start?.tierLabel,
    difficulty: start?.difficulty,
  };
}

function tsaSitting(set, sections) {
  const { s1: paper1 = null, s2: paper2 = null } = sections;
  const start = paper1 || paper2;
  const year = yearOf(setText(set));
  const q1 = Number(paper1?.question_count);
  const q2 = Number(paper2?.question_count);
  return {
    kind: 'sitting',
    tsaSitting: true,
    pearsonSitting: true,
    paper1,
    paper2,
    startId: start?.id,
    startCatalogId: start?.catalog_id || start?.id,
    title: year ? `TSA ${year}` : (start?.display_name || start?.id),
    questionCount: (Number.isFinite(q1) ? q1 : 0) + (Number.isFinite(q2) ? q2 : 0),
    duration: paper1 && paper2 ? '120 min' : (start?.duration || '90 min'),
    completed: false,
    tier: start?.tier,
    tierLabel: start?.tierLabel,
    difficulty: start?.difficulty,
    heritageExam: 'TSA',
  };
}

// TARA's specimen and sample: the server files each module as a set of its
// own (PLAN §3.4), so the module is read from the set's title, and the three
// sets of one paper are one sitting, as they have always been shown.
const TARA_TITLE_MODULES = [
  ['critical_thinking', /\bCritical Thinking\b/i],
  ['problem_solving', /\bProblem Solving\b/i],
  ['writing', /\bWrit(?:ing|ten) Task\b/i],
];

function taraTitleModule(set) {
  const hit = TARA_TITLE_MODULES.find(([, pattern]) => pattern.test(set.title));
  return hit ? hit[0] : null;
}

/**
 * TARA's sittings: a mock set holding all three modules; a BMAT or TSA past
 * set (BMAT by its sections, TSA Section 1 then 2); TARA's one-module past
 * sets of one paper together. The parts of anything else on their own.
 */
export function buildTaraSittings(parts) {
  const out = [];
  const pending = new Map();
  groupCatalogueSets(parts).forEach((set) => {
    const heritage = taraHeritage(set);
    const bmat = heritage === 'BMAT' ? partsByModule(set, BMAT_MODULE_KEYS) : null;
    if (bmat) {
      out.push(bmatSitting(set, bmat));
      return;
    }
    const tsa = heritage === 'TSA' ? partsByModule(set, TSA_SECTIONS) : null;
    if (tsa) {
      out.push(tsaSitting(set, tsa));
      return;
    }
    if (set.parts.length === 1 && set.parts[0].category === 'past_papers') {
      const module = taraTitleModule(set);
      const stem = sittingTitleFromTaraDisplayName(set.title);
      if (module && stem) {
        let group = pending.get(stem);
        if (!group) {
          group = { title: set.title, modules: {}, parts: [] };
          pending.set(stem, group);
          out.push(group);
        }
        if (!group.modules[module]) group.modules[module] = set.parts[0];
        group.parts.push(set.parts[0]);
        return;
      }
    }
    if (wholeSet(set, TARA_MODULE_KEYS)) {
      out.push(taraSitting(set.title, partsByModule(set, TARA_MODULE_KEYS)));
      return;
    }
    out.push(...set.parts.map(asPaper));
  });
  return out.flatMap((entry) => {
    if (!entry || !entry.modules) return [entry];
    const whole = TARA_MODULE_KEYS.every((key) => entry.modules[key]);
    return whole ? [taraSitting(entry.title, entry.modules)] : entry.parts.map(asPaper);
  });
}

/**
 * A set's name as the page names the sitting, with its exam: "TMUA
 * Foundations Mock 1" from either "TMUA Foundations Mock 1 — Paper 1" (a set
 * the backfill titled from its first part) or "TMUA Foundations Mock 1".
 */
export function sittingNameForSet(exam, title) {
  const examName = String(exam || '').toUpperCase();
  if (examName === 'TMUA') return sittingTitleFromDisplayName(title);
  if (examName === 'LNAT') return sittingTitleFromLnatDisplayName(title);
  if (examName === 'UCAT') return `UCAT ${sittingTitleFromUcatDisplayName(title)}`.trim();
  if (examName === 'ESAT') return sittingTitleFromEsatDisplayName(title);
  if (examName === 'TARA') return sittingTitleFromTaraDisplayName(title);
  return String(title || '').trim();
}
